import { useEffect } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import { useWorkspaceNavigate as useNavigate } from '@/contexts/DocumentWorkspace';
import AuthForm from '../components/AuthForm';
import { signOut } from '../lib/auth';
import { isDesktop } from '../lib/platform';

// Only allow same-origin relative paths as `next` targets to avoid open-redirect
// abuse (e.g. /login?next=https://evil.com).
function safeNextPath(raw) {
  if (!raw) return null;
  if (typeof raw !== 'string') return null;
  if (!raw.startsWith('/') || raw.startsWith('//')) return null;
  return raw;
}

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const desktop = isDesktop();

  // /register is an alias to /login with signup preselected. ?tab=signin|signup
  // still wins over the path if explicitly set.
  const initialTab = searchParams.get('tab') === 'signup'
    ? 'signup'
    : searchParams.get('tab') === 'signin'
      ? 'signin'
      : location.pathname === '/register'
        ? 'signup'
        : 'signin';
  const nextPath = safeNextPath(searchParams.get('next'));
  const defaultDest = nextPath || '/tools';

  // Finalize sign-out if the marketing nav redirected here with ?signout=1.
  // The marketing script already cleared scrivar_session; this drops the
  // Firebase session so the user is fully signed out.
  useEffect(() => {
    if (searchParams.get('signout') === '1') {
      signOut().catch(() => {});
    }
  }, [searchParams]);

  return (
    <LoginShell>
      <AuthForm
        defaultTab={initialTab}
        onSignedIn={() => navigate(desktop && !nextPath ? '/onboarding' : defaultDest)}
        showCodeFootnote
        continueWithoutAccount={!desktop && (
          <p className="mt-1 text-center text-xs text-gray-400 leading-loose">
            Free web users can use Scrivar without an account.{' '}
            <Link to="/tools" className="text-gray-600 hover:text-gray-800 underline inline-flex items-center justify-center min-h-[44px] py-3 px-3">
              Continue without signing in
            </Link>
          </p>
        )}
      />
    </LoginShell>
  );
}

function LoginShell({ children }) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-8 sm:py-12 px-4">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-6 sm:mb-8">
          <div
            className="w-12 h-12 rounded-xl mb-3 flex items-center justify-center text-white font-extrabold text-xl shadow-sm select-none"
            style={{ background: 'linear-gradient(135deg, #4F46E5, #6366F1)' }}
            aria-hidden="true"
          >
            S
          </div>
          <span className="text-sm font-medium text-gray-700">Scrivar</span>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {children}
        </div>
      </div>
    </div>
  );
}
